/**
 * Minimal Ogg encapsulation for Opus packets (RFC 3533 + RFC 7845), enough
 * for OpenAI-compatible transcription uploads: one mono logical stream,
 * OpusHead + OpusTags header pages, then audio pages with correct lacing,
 * granule positions, and page checksums.
 *
 * WebCodecs' AudioEncoder emits bare Opus packets with no container — this
 * is the missing piece, kept dependency-free (ffmpeg.wasm would force
 * 'wasm-unsafe-eval' into the CSP's script-src).
 */

export interface OggOpusPacket {
  data: Uint8Array;
  /** Packet duration in 48 kHz samples — the Ogg-Opus granule domain,
   *  regardless of the encoder's input sample rate (RFC 7845 §4). */
  granuleSamples: number;
}

/** Max lacing values per page (RFC 3533: one-byte segment count). */
const PAGE_MAX_SEGMENTS = 255;

/** RFC 7845 §4.2: decoder-discarded priming samples; libopus lookahead at
 *  48 kHz. Off-by-a-few only shifts trim by <10 ms — irrelevant for ASR. */
const PRE_SKIP_48K = 312;

/** Single logical stream per file, so a fixed serial is fine ("wend"). */
const STREAM_SERIAL = 0x77656e64;

// No continued-page flag (0x01) support: Opus packets max out ~1.3 KB at
// these settings, so a packet never has to span pages.
const PAGE_FLAG_BOS = 0x02;
const PAGE_FLAG_EOS = 0x04;

// Ogg CRC-32: poly 0x04c11db7, init 0, no reflection, no final xor —
// deliberately NOT the zlib variant.
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let bit = 0; bit < 8; bit++) {
      r = ((r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1) >>> 0);
    }
    table[i] = r;
  }
  return table;
})();

function oggCrc(bytes: Uint8Array): number {
  let crc = 0;
  for (let i = 0; i < bytes.length; i++) {
    crc = (((crc << 8) >>> 0) ^ CRC_TABLE[((crc >>> 24) & 0xff) ^ bytes[i]]) >>> 0;
  }
  return crc;
}

function buildPage(
  packets: Uint8Array[],
  flags: number,
  granulePosition: number,
  sequence: number,
): Uint8Array {
  const lacing: number[] = [];
  for (const packet of packets) {
    let remaining = packet.length;
    while (remaining >= 255) {
      lacing.push(255);
      remaining -= 255;
    }
    lacing.push(remaining); // 0–254 terminates the packet
  }
  if (lacing.length > PAGE_MAX_SEGMENTS) {
    throw new Error("ogg-opus: page overflow — packet set needs too many lacing values");
  }

  const bodyLength = packets.reduce((sum, packet) => sum + packet.length, 0);
  const page = new Uint8Array(27 + lacing.length + bodyLength);
  const view = new DataView(page.buffer);

  page.set([0x4f, 0x67, 0x67, 0x53]); // "OggS"
  page[4] = 0; // stream structure version
  page[5] = flags;
  // 64-bit LE granule position; values here stay far below 2^53.
  view.setUint32(6, granulePosition % 0x1_0000_0000, true);
  view.setUint32(10, Math.floor(granulePosition / 0x1_0000_0000), true);
  view.setUint32(14, STREAM_SERIAL, true);
  view.setUint32(18, sequence, true);
  // CRC (offset 22) is computed over the page with the field zeroed.
  page[26] = lacing.length;
  page.set(lacing, 27);
  let offset = 27 + lacing.length;
  for (const packet of packets) {
    page.set(packet, offset);
    offset += packet.length;
  }
  view.setUint32(22, oggCrc(page), true);
  return page;
}

function opusHead(inputSampleRate: number): Uint8Array {
  const head = new Uint8Array(19);
  const view = new DataView(head.buffer);
  head.set([0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64]); // "OpusHead"
  head[8] = 1; // version
  head[9] = 1; // channel count: mono
  view.setUint16(10, PRE_SKIP_48K, true);
  view.setUint32(12, inputSampleRate, true);
  view.setInt16(16, 0, true); // output gain
  head[18] = 0; // channel mapping family
  return head;
}

function opusTags(): Uint8Array {
  const vendor = new TextEncoder().encode("wendai-byok");
  const tags = new Uint8Array(8 + 4 + vendor.length + 4);
  const view = new DataView(tags.buffer);
  tags.set([0x4f, 0x70, 0x75, 0x73, 0x54, 0x61, 0x67, 0x73]); // "OpusTags"
  view.setUint32(8, vendor.length, true);
  tags.set(vendor, 12);
  view.setUint32(12 + vendor.length, 0, true); // zero user comments
  return tags;
}

/** Wrap encoded Opus packets into a complete, seekable Ogg byte stream. */
export function muxOggOpus(
  packets: OggOpusPacket[],
  inputSampleRate: number,
): Uint8Array<ArrayBuffer> {
  if (packets.length === 0) {
    throw new Error("ogg-opus: cannot mux an empty packet list");
  }

  const pages: Uint8Array[] = [
    buildPage([opusHead(inputSampleRate)], PAGE_FLAG_BOS, 0, 0),
    buildPage([opusTags()], 0, 0, 1),
  ];

  let sequence = 2;
  // Granule positions count decoded 48 kHz samples including pre-skip.
  let granule = PRE_SKIP_48K;
  let pagePackets: Uint8Array[] = [];
  let pageSegments = 0;

  for (let i = 0; i < packets.length; i++) {
    const segmentsNeeded = Math.floor(packets[i].data.length / 255) + 1;
    if (pagePackets.length > 0 && pageSegments + segmentsNeeded > PAGE_MAX_SEGMENTS) {
      pages.push(buildPage(pagePackets, 0, granule, sequence++));
      pagePackets = [];
      pageSegments = 0;
    }
    pagePackets.push(packets[i].data);
    pageSegments += segmentsNeeded;
    granule += packets[i].granuleSamples;
  }
  pages.push(buildPage(pagePackets, PAGE_FLAG_EOS, granule, sequence));

  const total = pages.reduce((sum, page) => sum + page.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const page of pages) {
    out.set(page, offset);
    offset += page.length;
  }
  return out;
}
